import React from 'react';
import {
  CALL_LOGS_COLUMNS,
  CL_TOKENS,
  INTENT_META,
  OUTBOUND_OUTCOME_META,
  OUTCOME_META,
  RECALL_TYPE_META,
  STATUS_META,
} from '../../receptionist-call-logs/config';
import type {
  CallLogsTabId,
  InboundCall,
  OutboundPatientCall,
} from '../../receptionist-call-logs';
import { Icon } from '../Icon/Icon';
import { ConfigPill, PatientAvatar, SentimentDot, getInitials } from './CallLogsShared';

function PlayButton({ isSelected, onClick, disabled }: { isSelected: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      className="inline-flex h-7 w-7 items-center justify-center rounded-full border-0 transition-[background,color] duration-150"
      style={{
        background: isSelected ? CL_TOKENS.green : CL_TOKENS.surfaceMute,
        color: isSelected ? '#fff' : CL_TOKENS.ink2,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
      title="Play recording"
    >
      <Icon kind="play" size={10} />
    </button>
  );
}

function NameCell({ name, phone }: { name: string; phone: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <PatientAvatar size={32} initials={name === 'Unknown caller' ? null : getInitials(name)} />
      <div className="min-w-0 flex-1">
        <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
          {name}
        </div>
        <div className="cl-mono mt-px text-[11px] font-medium" style={{ color: CL_TOKENS.ink3 }}>
          {phone}
        </div>
      </div>
    </div>
  );
}

function rowStyle(template: string, isSelected: boolean, isLast: boolean): React.CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: template,
    gap: 14,
    alignItems: 'center',
    padding: '11px 22px',
    paddingLeft: isSelected ? 19 : 22,
    borderBottom: isLast ? 'none' : `1px solid ${CL_TOKENS.border}`,
    background: isSelected ? CL_TOKENS.greenWash : 'transparent',
    borderLeft: isSelected ? `3px solid ${CL_TOKENS.green}` : '3px solid transparent',
  };
}

function InboundRow({
  call,
  isSelected,
  isLast,
  onSelect,
}: {
  call: InboundCall;
  isSelected: boolean;
  isLast: boolean;
  onSelect: () => void;
}) {
  return (
    <div className="cl-row" onClick={onSelect} style={rowStyle(CALL_LOGS_COLUMNS.inbound.template, isSelected, isLast)}>
      <NameCell name={call.name} phone={call.phone} />
      <ConfigPill meta={INTENT_META[call.intent]} />
      <ConfigPill meta={OUTCOME_META[call.outcome]} />
      <SentimentDot sentiment={call.sentiment} />
      <span className="cl-mono text-right text-[12px] font-medium" style={{ color: CL_TOKENS.ink2 }}>
        {call.duration}
      </span>
      <span
        className="overflow-hidden text-ellipsis whitespace-nowrap text-right text-[11.5px] font-medium tracking-[-0.005em]"
        style={{ color: CL_TOKENS.ink3 }}
      >
        {call.timeLabel.replace('Today, ', '')}
      </span>
      <PlayButton isSelected={isSelected} onClick={onSelect} />
    </div>
  );
}

function OutboundPatientRow({
  call,
  isSelected,
  isLast,
  onSelect,
}: {
  call: OutboundPatientCall;
  isSelected: boolean;
  isLast: boolean;
  onSelect: () => void;
}) {
  const isFinal = call.attemptNumber === call.maxAttempts;
  const outcomeMeta =
    call.status === 'completed' ? OUTBOUND_OUTCOME_META[call.primaryOutcome] : STATUS_META[call.status];
  return (
    <div className="cl-row" onClick={onSelect} style={rowStyle(CALL_LOGS_COLUMNS.outboundPatient.template, isSelected, isLast)}>
      <NameCell name={call.name} phone={call.phone} />
      <ConfigPill meta={RECALL_TYPE_META[call.recallContext.recallTypeSlug]} />
      <span
        className="cl-mono cl-tab-num text-center text-[12px]"
        style={{ color: isFinal ? CL_TOKENS.ink1 : CL_TOKENS.ink2, fontWeight: isFinal ? 700 : 600 }}
      >
        {call.attemptNumber}/{call.maxAttempts}
      </span>
      <ConfigPill meta={outcomeMeta} />
      <span className="cl-mono text-right text-[12px] font-medium" style={{ color: CL_TOKENS.ink2 }}>
        {call.duration}
      </span>
      <span
        className="overflow-hidden text-ellipsis whitespace-nowrap text-right text-[11.5px] font-medium tracking-[-0.005em]"
        style={{ color: CL_TOKENS.ink3 }}
      >
        {call.timeLabel.replace('Today, ', '')}
      </span>
      <PlayButton isSelected={isSelected} onClick={onSelect} disabled={!call.transcript && !call.voicemailMessage} />
    </div>
  );
}

function ListHeader({ activeTab }: { activeTab: CallLogsTabId }) {
  const columns = CALL_LOGS_COLUMNS[activeTab];
  return (
    <div
      className="grid items-center gap-3.5 border-b border-[var(--cream-2)] px-[22px] py-[9px]"
      style={{ gridTemplateColumns: columns.template, background: CL_TOKENS.surfaceSubtle }}
    >
      {columns.labels.map((label, i) => (
        <span
          key={i}
          className="cl-eyebrow text-[9.5px]"
          style={{
            textAlign: i >= columns.labels.length - 3 ? 'right' : 'left',
            color: CL_TOKENS.ink4,
          }}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function EmptyListState({ activeTab }: { activeTab: CallLogsTabId }) {
  const message =
    activeTab === 'inbound'
      ? 'No inbound calls in this time range.'
      : 'No outbound patient calls in this time range.';
  return (
    <section className="cl-fade-up cl-d-3 flex flex-col items-center gap-[22px] overflow-hidden rounded-2xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-8 pb-14 pt-16 text-center shadow-[var(--sh-1)]">
      <div className="relative flex h-24 w-24 items-center justify-center">
        <div className="cl-listen-ring" style={{ animationDelay: '0s' }} />
        <div className="cl-listen-ring" style={{ animationDelay: '1.07s' }} />
        <div className="cl-listen-ring" style={{ animationDelay: '2.13s' }} />
        <div
          className="relative z-[3] h-2.5 w-2.5 rounded-full"
          style={{
            background: CL_TOKENS.iriDot,
            boxShadow: '0 0 14px rgba(15, 92, 69, 0.3), 0 0 28px rgba(15, 92, 69, 0.15)',
          }}
        />
      </div>
      <div>
        <h2 className="m-0 mb-1.5 text-[18px] font-semibold leading-[1.2] tracking-[-0.02em] text-[var(--ink-0)]">
          {message}
        </h2>
        <p className="m-0 max-w-[44ch] text-[13px] leading-[1.55] tracking-[-0.005em]" style={{ color: CL_TOKENS.ink3 }}>
          Try widening the date range or clearing your filters.
        </p>
      </div>
    </section>
  );
}

export interface CallLogsTableProps {
  activeTab: CallLogsTabId;
  inboundCalls: InboundCall[];
  outboundCalls: OutboundPatientCall[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function CallLogsTable({
  activeTab,
  inboundCalls,
  outboundCalls,
  selectedId,
  onSelect,
}: CallLogsTableProps) {
  const count = activeTab === 'inbound' ? inboundCalls.length : outboundCalls.length;
  if (count === 0) {
    return <EmptyListState activeTab={activeTab} />;
  }
  return (
    <section className="cl-fade-up cl-d-3 mb-3.5 overflow-hidden rounded-2xl border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
      <ListHeader activeTab={activeTab} />
      <div>
        {activeTab === 'inbound'
          ? inboundCalls.map((call, i) => (
              <InboundRow
                key={call.id}
                call={call}
                isSelected={selectedId === call.id}
                isLast={i === inboundCalls.length - 1}
                onSelect={() => onSelect(call.id)}
              />
            ))
          : outboundCalls.map((call, i) => (
              <OutboundPatientRow
                key={call.id}
                call={call}
                isSelected={selectedId === call.id}
                isLast={i === outboundCalls.length - 1}
                onSelect={() => onSelect(call.id)}
              />
            ))}
      </div>
    </section>
  );
}
